import { ApprovableType, ModelApprovalable } from 'api-hooks/general/model';
import { useRevertDraft } from 'api-hooks/general/mutation';
import { AuthorizationRules } from 'common/constants';
import {
  SaleStatusEnum,
  ProjectStatusEnum,
  TourStatusEnum,
  RefundStatusEnum,
} from 'common/constants/enum';
import notification from 'components/common/notification';
import { Button } from 'components/elements/buttons';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import { useAuthorization } from './use-authorization';
import useDialog from './use-dialog';
import useDismiss from './use-dismiss';
import useNavigation from './use-navigation';

export default function useRevertDraftAction(
  type: ApprovableType,
  data?: ModelApprovalable,
) {
  const id = data?.id;
  const status = data?.status;
  const isDraft = React.useMemo(() => {
    switch (status!) {
      case SaleStatusEnum.Draft:
      case SaleStatusEnum.RescheduleDraft:
      case SaleStatusEnum.VoidDraft:
      case SaleStatusEnum.RevertVoidDraft:
      case ProjectStatusEnum.CancelDraft:
      case TourStatusEnum.CloseDraft:
      case TourStatusEnum.RevertCloseDraft:
      case RefundStatusEnum.DraftCancel:
        return true;
      default:
        return false;
    }
  }, [status]);

  const { can } = useAuthorization();
  const { t } = useTranslation();
  const dialog = useDialog();
  const revertDraftMutate = useRevertDraft();
  const onDismiss = useDismiss(type);
  const { close } = useNavigation();

  const revertDraftPermission = React.useMemo(() => {
    switch (type) {
      case 'account-payables':
        return can(AuthorizationRules.AccountPayablesRevertDraft);
      case 'account-receivables':
        return can(AuthorizationRules.AccountReceivablesRevertDraft);
      case 'activity-purchases':
        return can(AuthorizationRules.ActivityPurchasesRevertDraft);
      case 'activity-sales':
        return can(AuthorizationRules.ActivitySalesRevertDraft);
      case 'balance-adjustments':
        return can(AuthorizationRules.BalanceAdjustmentsRevertDraft);
      case 'bank-transactions':
        return can(AuthorizationRules.BankTransactionsRevertDraft);
      case 'cash-transactions':
        return can(AuthorizationRules.CashTransactionsRevertDraft);
      case 'cruise-purchases':
        return can(AuthorizationRules.CruisePurchasesRevertDraft);
      case 'cruise-sales':
        return can(AuthorizationRules.CruiseSalesRevertDraft);
      case 'document-purchases':
        return can(AuthorizationRules.DocumentPurchasesRevertDraft);
      case 'document-sales':
        return can(AuthorizationRules.DocumentSalesRevertDraft);
      case 'flight-purchases':
        return can(AuthorizationRules.FlightPurchasesRevertDraft);
      case 'flight-sales':
        return can(AuthorizationRules.FlightSalesRevertDraft);
      case 'hotel-purchases':
        return can(AuthorizationRules.HotelPurchasesRevertDraft);
      case 'hotel-sales':
        return can(AuthorizationRules.HotelSalesRevertDraft);
      case 'order-adjustments':
        return can(AuthorizationRules.OrderAdjustmentsRevertDraft);
      case 'other-expenses':
        return can(AuthorizationRules.OtherExpensesRevertDraft);
      case 'other-incomes':
        return can(AuthorizationRules.OtherIncomesRevertDraft);
      case 'product-purchases':
        return can(AuthorizationRules.ProductPurchasesRevertDraft);
      case 'product-sales':
        return can(AuthorizationRules.ProductSalesRevertDraft);
      case 'projects':
        return can(AuthorizationRules.ProjectsRevertDraft);
      case 'receipts':
        return can(AuthorizationRules.ReceiptsRevertDraft);
      case 'tour-sales':
        return can(AuthorizationRules.TourSalesRevertDraft);
      case 'refunds':
        return can(AuthorizationRules.RefundsRevertDraft);
      case 'release-sales':
        return can(AuthorizationRules.ReleaseSalesRevertDraft);
      case 'release-purchases':
        return can(AuthorizationRules.ReleasePurchasesRevertDraft);
      case 'transfers':
        return can(AuthorizationRules.TransfersRevertDraft);
      case 'tours':
        return can(AuthorizationRules.ToursRevertDraft);
    }
  }, [can, type]);

  const onCloseTab = React.useCallback(() => {
    switch (status!) {
      case SaleStatusEnum.Draft:
      case SaleStatusEnum.RescheduleDraft:
        return close();
      default:
        return undefined;
    }
  }, [close, status]);

  const onRevertDraft = React.useCallback(
    async (id: string) => {
      const result = await revertDraftMutate.mutateAsync({ type, id });
      notification.success({
        message: result.message,
      });
      onDismiss(id);
      return result;
    },
    [onDismiss, revertDraftMutate, type],
  );

  const onClickRevertDraft = React.useCallback(
    (id: string) => () => {
      return new Promise((resolve, reject) => {
        dialog.showConfirmation({
          message: t('common:revert_draft_description'),
          title: t('common:revert_draft'),
          async onPositiveAction(dismiss) {
            try {
              const result = await onRevertDraft(id);
              resolve(result);
              onCloseTab();
              dismiss();
            } catch (e) {
              notification.error({
                message: e.message,
              });
              reject(e);
            }
          },
          onNegativeAction(dismiss) {
            dismiss();
          },
          onAfterClose() {
            resolve(undefined);
          },
        });
      });
    },
    [dialog, onCloseTab, onRevertDraft, t],
  );

  const revertDraftButton = revertDraftPermission && isDraft && id && (
    <Button
      onClick={onClickRevertDraft(id)}
      isLoading={revertDraftMutate.isLoading}
    >
      {t('common:revert_draft')}
    </Button>
  );

  return {
    ...revertDraftMutate,
    onClickRevertDraft,
    onRevertDraft,
    revertDraftPermission,
    revertDraftButton,
    isDraft,
  };
}
